import { app } from 'app/providers/FirebaseProvider/ui/FirebaseProvider'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import React, { useEffect, useRef, useState } from 'react'
import {
    createDomainSessionTransfer,
    prepareDomainMigrationProfile,
    readTransferredDomainMigrationProfile,
    redeemDomainSessionTransfer,
} from 'shared/api/domainSessionTransfer'
import logoWhale from 'shared/assets/MineCards-Logo.svg'
import whaleGreeting from 'shared/assets/images/whaleGreeting.png'
import installInstruction from 'shared/assets/install.png'
import { supportUrl } from 'shared/config/externalLinks'
import styles from './DomainMigrationGate.module.scss'
import { emailAuthEnabled } from 'features/emailAuth/config'
import { EmailAuthFlow } from 'features/emailAuth'
import { getEmailLink } from 'shared/api/emailAuth'
import { ImageWithSkeleton } from 'shared/ui/ImageWithSkeleton'
import { ExportIcon } from 'shared/ui/Icon'
import { isStandalonePwa } from 'shared/lib/isStandalonePwa/isStandalonePwa'

const legacyHosts = new Set(['taglife.net', 'www.taglife.net'])
const primaryHosts = new Set(['minecards.app', 'www.minecards.app'])
const defaultPrimaryOrigin = 'https://minecards.app'
const legacyOrigins = ['https://taglife.net', 'https://www.taglife.net']

const validProfileId = (value) => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    const profileId = Number(value)
    return Number.isSafeInteger(profileId) && profileId >= 0 ? profileId : null
}

export const domainMigrationState = ({ hostname, pathname, search, hash }) => {
    const normalizedHost = String(hostname || '').toLowerCase()
    if (legacyHosts.has(normalizedHost)) {
        return { mode: 'legacy' }
    }
    if (primaryHosts.has(normalizedHost) && pathname === '/migrate') {
        const params = new URLSearchParams(String(hash || '').replace(/^#/, ''))
        const code = params.get('code')
        const profileId = validProfileId(params.get('profile'))
        if (code) {
            return { mode: 'redeem', code, profileId }
        }
        const resumeProfileId = validProfileId(
            new URLSearchParams(String(search || '')).get('profile')
        )
        if (resumeProfileId !== null) {
            return { mode: 'resume', profileId: resumeProfileId }
        }
        return { mode: 'invalid' }
    }
    if (primaryHosts.has(normalizedHost)) {
        return { mode: 'primary' }
    }
    return { mode: 'app' }
}

const profileIdFromPath = (pathname) => {
    const match = String(pathname || '').match(/^\/(\d+)\/?$/)
    if (!match) {
        return null
    }
    const profileId = Number(match[1])
    return Number.isSafeInteger(profileId) ? profileId : null
}

const MigrationScreen = ({ title, children }) => (
    <main className={`${styles.page} normal`}>
        <section aria-live="polite" className={styles.card}>
            <div className={styles.brand}>
                <img alt="" src={logoWhale} />
                <span>MineCards</span>
            </div>
            <img alt="" className={styles.illustration} src={whaleGreeting} />
            <div className={styles.eyebrow}>Переезд приложения</div>
            <h1>{title}</h1>
            <div className={styles.content}>{children}</div>
        </section>
    </main>
)

const listLength = (value) => (Array.isArray(value) ? value.length : 0)

const countLabel = (count, [one, few, many]) => {
    const modulo100 = count % 100
    const modulo10 = count % 10
    const word =
        modulo100 >= 11 && modulo100 <= 14
            ? many
            : modulo10 === 1
              ? one
              : modulo10 >= 2 && modulo10 <= 4
                ? few
                : many
    return `${count} ${word}`
}

const ProfilePreview = ({ profile, showStats = false }) => {
    const momentsCount =
        listLength(profile.acceptedMoments) +
        listLength(profile.wantedMoments) +
        listLength(profile.rejectedMoments)
    const profileAddress = profile.nickname
        ? `@${profile.nickname}`
        : `minecards.app/${profile.id}`
    const hasStats =
        Array.isArray(profile.acceptedMoments) ||
        Array.isArray(profile.wantedMoments) ||
        Array.isArray(profile.rejectedMoments) ||
        Array.isArray(profile.friends)

    return (
        <section className={styles.profilePreview} aria-label="Ваш профиль">
            <ImageWithSkeleton
                alt=""
                className={styles.profileAvatar}
                onError={(event) => {
                    event.currentTarget.onerror = null
                    event.currentTarget.src = logoWhale
                }}
                src={profile.avatar || logoWhale}
            />
            <div className={styles.profileIdentity}>
                <strong>{profile.name || 'Профиль MineCards'}</strong>
                <span>{profileAddress}</span>
            </div>
            {profile.isPremium && (
                <span className={styles.premiumBadge}>Premium</span>
            )}
            {showStats && hasStats && (
                <div className={styles.profileStats}>
                    <span>
                        {countLabel(momentsCount, [
                            'момент',
                            'момента',
                            'моментов',
                        ])}
                    </span>
                    <span>
                        {countLabel(listLength(profile.friends), [
                            'друг',
                            'друга',
                            'друзей',
                        ])}
                    </span>
                </div>
            )}
        </section>
    )
}

const failureCopy = (error, phase) => {
    if (error?.code === 'network_error') {
        return {
            title: 'Не удалось подключиться',
            message: 'Проверьте интернет и попробуйте ещё раз.',
        }
    }
    if (error?.code === 'profile_not_found' || error?.status === 404) {
        return {
            title: 'Не нашли ваш профиль',
            message:
                'Сессия сохранилась, но профиль не найден среди перенесённых данных. Напишите нам — мы проверим его вручную.',
            support: true,
        }
    }
    if (error?.code === 'unauthorized' || error?.status === 401) {
        return {
            title: 'Не удалось подтвердить вход',
            message:
                'Откройте старое приложение там же, где вы пользовались им раньше.',
        }
    }
    if (error?.code === 'transfer_rate_limited' || error?.status === 429) {
        return {
            title: 'Подождите пару секунд',
            message: 'Мы уже начали перенос. Немного подождите и повторите.',
        }
    }
    if (error?.code === 'invalid_transfer_code' || error?.status === 410) {
        return {
            title: 'Ссылка переноса устарела',
            message:
                'Вернитесь в старое приложение и нажмите «Перенести профиль» ещё раз.',
        }
    }
    return {
        title:
            phase === 'profile'
                ? 'Не удалось проверить профиль'
                : 'Не удалось закончить перенос',
        message:
            'Попробуйте ещё раз. Если ошибка повторится, напишите нам — ваши данные останутся без изменений.',
        support: true,
    }
}

const MigrationFailure = ({ error, onRetry, phase }) => {
    const copy = failureCopy(error, phase)
    return (
        <MigrationScreen title={copy.title}>
            <p>{copy.message}</p>
            <button className={styles.button} onClick={onRetry} type="button">
                Проверить ещё раз
            </button>
            {copy.support && (
                <a className={styles.secondaryButton} href={supportUrl}>
                    Написать нам
                </a>
            )}
            {error?.requestId && (
                <p className={styles.requestId}>
                    Код обращения: <code>{error.requestId}</code>
                </p>
            )}
        </MigrationScreen>
    )
}

export const installPlatform = (navigatorImpl = window.navigator) => {
    if (isStandalonePwa()) {
        return 'standalone'
    }
    const userAgent = String(navigatorImpl?.userAgent || '')
    if (/iPad|iPhone|iPod/i.test(userAgent)) {
        return 'ios'
    }
    if (/Android/i.test(userAgent)) {
        return 'android'
    }
    return 'browser'
}

const MigrationInstallStep = ({ account, installPrompt, onContinue }) => {
    const [installStatus, setInstallStatus] = useState('idle')
    const platform = installPlatform()

    const install = async () => {
        if (typeof installPrompt?.prompt !== 'function') {
            return
        }
        setInstallStatus('working')
        try {
            await installPrompt.prompt()
            const choice = await installPrompt.userChoice
            setInstallStatus(
                choice?.outcome === 'accepted' ? 'installed' : 'dismissed'
            )
        } catch (error) {
            console.error('MineCards installation prompt failed', error)
            setInstallStatus('idle')
        }
    }

    return (
        <MigrationScreen title="Аккаунт готов!">
            <p>
                Профиль сохранён на почту <strong>{account?.email}</strong>.
                После установки войдите по этой почте — все данные будут на
                месте.
            </p>
            {platform === 'ios' && (
                <section
                    aria-label="Как установить MineCards"
                    className={styles.installInstruction}
                >
                    <img
                        alt="Пункт На экран Домой в меню браузера"
                        className={styles.installImage}
                        src={installInstruction}
                    />
                    <strong>Установите новый MineCards</strong>
                    <span>
                        Нажмите «Поделиться»{' '}
                        <ExportIcon
                            className={styles.installShareIcon}
                            size={24}
                        />{' '}
                        и выберите «На экран „Домой“».
                    </span>
                </section>
            )}
            {platform === 'android' &&
                installPrompt &&
                installStatus !== 'installed' &&
                installStatus !== 'dismissed' && (
                    <button
                        className={styles.button}
                        disabled={installStatus === 'working'}
                        onClick={install}
                        type="button"
                    >
                        {installStatus === 'working'
                            ? 'Открываем установку…'
                            : 'Установить MineCards'}
                    </button>
                )}
            {platform === 'android' &&
                (!installPrompt || installStatus === 'dismissed') && (
                    <p className={styles.installHint}>
                        Если кнопка установки не появилась, откройте меню
                        браузера и выберите «Установить приложение».
                    </p>
                )}
            {installStatus === 'installed' && (
                <p className={styles.installSuccess} role="status">
                    MineCards установлен. Откройте его с главного экрана и
                    войдите по почте.
                </p>
            )}
            <button
                className={styles.secondaryButton}
                onClick={onContinue}
                type="button"
            >
                {platform === 'standalone'
                    ? 'Открыть MineCards'
                    : 'Продолжить на сайте'}
            </button>
        </MigrationScreen>
    )
}

const useFirebaseSession = () => {
    const [user, setUser] = useState(undefined)

    useEffect(() => {
        const auth = getAuth(app)
        return onAuthStateChanged(auth, (nextUser) => setUser(nextUser))
    }, [])

    return user
}

const LegacyMigration = ({ hostname, pathname, navigate }) => {
    const user = useFirebaseSession()
    const [profile, setProfile] = useState(undefined)
    const [account, setAccount] = useState(undefined)
    const [profileError, setProfileError] = useState(null)
    const [profileAttempt, setProfileAttempt] = useState(0)
    const [status, setStatus] = useState('idle')
    const [transferError, setTransferError] = useState(null)

    useEffect(() => {
        let active = true
        if (!user) {
            setProfile(undefined)
            setAccount(undefined)
            return () => {
                active = false
            }
        }

        setProfile(undefined)
        setAccount(undefined)
        setProfileError(null)
        const loadMigrationState = async () => {
            try {
                const nextProfile = await prepareDomainMigrationProfile(user)
                let nextAccount = null
                try {
                    const emailLink = await getEmailLink(user)
                    nextAccount = emailLink.linked
                        ? (emailLink.account ?? null)
                        : null
                } catch (error) {
                    console.error(
                        'Legacy migration email link check failed',
                        error
                    )
                }
                if (active) {
                    setProfile(nextProfile)
                    setAccount(nextAccount)
                }
            } catch (error) {
                console.error('Domain migration profile check failed', error)
                if (active) {
                    setProfile(null)
                    setAccount(null)
                    setProfileError(error)
                }
            }
        }
        loadMigrationState()
        return () => {
            active = false
        }
    }, [profileAttempt, user])

    const migrate = async () => {
        setStatus('working')
        setTransferError(null)
        try {
            const { code, profileId: transferProfileId } =
                await createDomainSessionTransfer(user)
            if (transferProfileId !== profile.id) {
                throw new Error('Transferred profile ID does not match')
            }
            const primaryOrigin = (
                process.env.REACT_APP_PRIMARY_ORIGIN || defaultPrimaryOrigin
            ).replace(/\/+$/, '')
            navigate(
                `${primaryOrigin}/migrate#code=${encodeURIComponent(code)}&profile=${profile.id}`
            )
        } catch (error) {
            console.error('Domain session transfer failed', error)
            setTransferError(error)
            setStatus('error')
        }
    }

    if (user === undefined) {
        return (
            <MigrationScreen title="Готовим перенос">
                <p>Проверяем сохранённую сессию на этом устройстве…</p>
            </MigrationScreen>
        )
    }
    if (!user) {
        const otherLegacyOrigin = legacyOrigins.find(
            (origin) => new URL(origin).hostname !== hostname
        )
        return (
            <MigrationScreen title="Не нашли вход в старое приложение">
                <p>
                    Откройте установленное старое приложение на этом устройстве.
                    Если раньше пользовались сайтом, откройте его в том же
                    браузере.
                </p>
                {otherLegacyOrigin && (
                    <a
                        className={styles.secondaryButton}
                        href={otherLegacyOrigin}
                    >
                        Проверить другой старый адрес
                    </a>
                )}
                <p className={styles.hint}>
                    Простой переход на новый адрес не восстановит прежний
                    профиль.
                </p>
            </MigrationScreen>
        )
    }

    if (profile === undefined) {
        return (
            <MigrationScreen title="Ищем ваш профиль">
                <p>Это займёт несколько секунд…</p>
            </MigrationScreen>
        )
    }

    if (profile === null) {
        return (
            <MigrationFailure
                error={profileError}
                onRetry={() => setProfileAttempt((value) => value + 1)}
                phase="profile"
            />
        )
    }

    const requestedProfileId = profileIdFromPath(pathname)
    const primaryOrigin = (
        process.env.REACT_APP_PRIMARY_ORIGIN || defaultPrimaryOrigin
    ).replace(/\/+$/, '')

    if (account) {
        return (
            <MigrationScreen title="Профиль уже в MineCards">
                <ProfilePreview profile={profile} showStats />
                <p>
                    Вход сохранён на почту <strong>{account.email}</strong>.
                    Повторно переносить данные не нужно.
                </p>
                <button
                    className={styles.button}
                    onClick={() => navigate(`${primaryOrigin}/`)}
                    type="button"
                >
                    Открыть новый MineCards
                </button>
                <p className={styles.hint}>
                    Если MineCards попросит войти, используйте эту почту. Старое
                    приложение после этого можно удалить.
                </p>
            </MigrationScreen>
        )
    }

    return (
        <MigrationScreen title="Ваш профиль готов к переезду">
            <ProfilePreview profile={profile} />
            {requestedProfileId !== null &&
                requestedProfileId !== profile.id && (
                    <p className={styles.warning} role="alert">
                        В адресе открыт профиль №{requestedProfileId}, но
                        текущая сессия принадлежит профилю №{profile.id}.
                        Перенесён будет профиль текущей сессии.
                    </p>
                )}
            <p>Все мечты, друзья, коллекции и Premium сохранятся.</p>
            <button
                className={styles.button}
                disabled={status === 'working'}
                onClick={migrate}
                type="button"
            >
                {status === 'working'
                    ? 'Переносим профиль…'
                    : 'Перенести профиль'}
            </button>
            <p className={styles.hint}>
                Старое приложение пока не удаляйте. Сначала убедимся, что всё
                открылось в MineCards.
            </p>
            {status === 'error' && (
                <>
                    <div className={styles.inlineError} role="alert">
                        <strong>{failureCopy(transferError).title}</strong>
                        <span>{failureCopy(transferError).message}</span>
                        {transferError?.requestId && (
                            <small>
                                Код обращения:{' '}
                                <code>{transferError.requestId}</code>
                            </small>
                        )}
                    </div>
                    {failureCopy(transferError).support && (
                        <a className={styles.secondaryButton} href={supportUrl}>
                            Написать нам
                        </a>
                    )}
                </>
            )}
        </MigrationScreen>
    )
}

const LegacyPrimarySessionGate = ({ children }) => {
    const user = useFirebaseSession()

    if (user === undefined) {
        return (
            <MigrationScreen title="Проверяем сессию">
                <p>Убеждаемся, что профиль уже перенесён…</p>
            </MigrationScreen>
        )
    }
    if (!user) {
        return (
            <MigrationScreen title="Сначала перенесите профиль">
                <p>
                    Откройте старое приложение или адрес, которым пользовались
                    раньше, и запустите перенос оттуда.
                </p>
                <div className={styles.legacyLinks}>
                    {legacyOrigins.map((origin) => (
                        <a
                            className={styles.secondaryButton}
                            href={origin}
                            key={origin}
                        >
                            {new URL(origin).hostname}
                        </a>
                    ))}
                </div>
            </MigrationScreen>
        )
    }
    return children
}

const PrimarySessionGate = ({ children }) =>
    emailAuthEnabled ? (
        children
    ) : (
        <LegacyPrimarySessionGate>{children}</LegacyPrimarySessionGate>
    )

const RedeemMigration = ({ code, expectedProfileId, history, children }) => {
    const [attempt, setAttempt] = useState(0)
    const [status, setStatus] = useState('working')
    const [profile, setProfile] = useState(null)
    const [account, setAccount] = useState(null)
    const [error, setError] = useState(null)
    const [installPrompt, setInstallPrompt] = useState(null)
    const completedProfileRef = useRef(null)

    useEffect(() => {
        if (installPlatform() === 'standalone') {
            return undefined
        }
        const capturePrompt = (event) => {
            event.preventDefault()
            setInstallPrompt(event)
        }
        window.addEventListener('beforeinstallprompt', capturePrompt)
        return () =>
            window.removeEventListener('beforeinstallprompt', capturePrompt)
    }, [])

    useEffect(() => {
        let active = true
        const complete = async (profileId, loadedProfile = null) => {
            const transferredProfile =
                loadedProfile || (await readTransferredDomainMigrationProfile())
            if (transferredProfile.id !== profileId) {
                throw new Error('Transferred profile ID does not match')
            }
            completedProfileRef.current = transferredProfile
            if (active) {
                history.replaceState(null, '', `/migrate?profile=${profileId}`)
                setProfile(transferredProfile)
                setStatus('checking-account')
            }

            let emailLink
            try {
                emailLink = await getEmailLink(null)
            } catch (accountError) {
                console.error(
                    'Domain migration email link check failed',
                    accountError
                )
                if (active) {
                    setError(accountError)
                    setStatus('account-error')
                }
                return
            }
            if (!active) {
                return
            }
            if (emailLink.linked) {
                setAccount(emailLink.account)
                setStatus('ready')
            } else {
                setStatus('account')
            }
        }
        const redeem = async () => {
            setStatus('working')
            setError(null)
            try {
                if (completedProfileRef.current) {
                    await complete(
                        completedProfileRef.current.id,
                        completedProfileRef.current
                    )
                    return
                }
                if (!code) {
                    const currentProfile =
                        await readTransferredDomainMigrationProfile()
                    if (currentProfile.id !== expectedProfileId) {
                        throw new Error('Transferred profile ID does not match')
                    }
                    await complete(currentProfile.id, currentProfile)
                    return
                }
                const result = await redeemDomainSessionTransfer(code)
                if (
                    expectedProfileId !== null &&
                    result.profileId !== expectedProfileId
                ) {
                    throw new Error('Transferred profile ID does not match')
                }
                await complete(result.profileId)
            } catch (redeemError) {
                console.error('Domain session redemption failed', redeemError)
                if (code && expectedProfileId !== null) {
                    try {
                        const currentProfile =
                            await readTransferredDomainMigrationProfile()
                        if (currentProfile.id === expectedProfileId) {
                            if (active) {
                                await complete(
                                    currentProfile.id,
                                    currentProfile
                                )
                            }
                            return
                        }
                    } catch {
                        // The transfer really failed; keep the original error.
                    }
                }
                if (active) {
                    setError(redeemError)
                    setStatus('error')
                }
            }
        }
        redeem()
        return () => {
            active = false
        }
    }, [attempt, code, expectedProfileId, history])

    if (status === 'open') {
        return children
    }
    if (status === 'ready') {
        return (
            <MigrationInstallStep
                account={account}
                installPrompt={installPrompt}
                onContinue={() => {
                    history.replaceState(null, '', '/')
                    setStatus('open')
                }}
            />
        )
    }
    if (status === 'account') {
        return (
            <MigrationScreen title="Профиль перенесён!">
                <ProfilePreview profile={profile} showStats />
                <p>
                    Теперь сохраните вход. Эта почта понадобится, чтобы войти в
                    новый PWA и не потерять профиль после удаления старого.
                </p>
                <EmailAuthFlow
                    mode="link"
                    onComplete={(result) => {
                        setAccount(result)
                        setStatus('ready')
                    }}
                    user={null}
                />
                <a className={styles.helpLink} href={supportUrl}>
                    Не получается? Напишите нам
                </a>
            </MigrationScreen>
        )
    }
    const accountCheckFailed = status === 'account-error'
    return (
        <MigrationScreen
            title={
                status === 'working'
                    ? 'Переносим профиль…'
                    : status === 'checking-account'
                      ? 'Проверяем аккаунт…'
                      : accountCheckFailed
                        ? 'Профиль перенесён, но почту не проверили'
                        : failureCopy(error, 'redeem').title
            }
        >
            <p>
                {status === 'working' || status === 'checking-account'
                    ? 'Не закрывайте страницу. Это займёт несколько секунд.'
                    : accountCheckFailed
                      ? 'Данные уже на месте. Проверьте интернет и повторите проверку, чтобы сохранить вход по почте.'
                      : failureCopy(error, 'redeem').message}
            </p>
            {(status === 'error' || accountCheckFailed) && (
                <>
                    <button
                        className={styles.button}
                        onClick={() => setAttempt((value) => value + 1)}
                        type="button"
                    >
                        Проверить ещё раз
                    </button>
                    {(accountCheckFailed ||
                        failureCopy(error, 'redeem').support) && (
                        <a className={styles.secondaryButton} href={supportUrl}>
                            Написать нам
                        </a>
                    )}
                    {error?.requestId && (
                        <p className={styles.requestId}>
                            Код обращения: <code>{error.requestId}</code>
                        </p>
                    )}
                </>
            )}
        </MigrationScreen>
    )
}

export const DomainMigrationGate = ({
    children,
    location = window.location,
    history = window.history,
    navigate,
}) => {
    const state = domainMigrationState(location)
    const navigateTo = navigate || ((url) => window.location.replace(url))

    if (state.mode === 'legacy') {
        return (
            <LegacyMigration
                hostname={location.hostname}
                navigate={navigateTo}
                pathname={location.pathname}
            />
        )
    }
    if (state.mode === 'redeem') {
        return (
            <RedeemMigration
                code={state.code}
                expectedProfileId={state.profileId}
                history={history}
            >
                {children}
            </RedeemMigration>
        )
    }
    if (state.mode === 'resume') {
        return (
            <RedeemMigration
                code={null}
                expectedProfileId={state.profileId}
                history={history}
            >
                {children}
            </RedeemMigration>
        )
    }
    if (state.mode === 'invalid') {
        return (
            <MigrationScreen title="Ссылка переноса недействительна">
                <p>Откройте старое приложение и запустите перенос ещё раз.</p>
            </MigrationScreen>
        )
    }
    if (state.mode === 'primary') {
        return <PrimarySessionGate>{children}</PrimarySessionGate>
    }
    return children
}
